import { useEffect, useRef } from 'react';

interface UsePollingOptions {
  enabled?: boolean;
  interval?: number;
  timeout?: number;
  onTimeout?: () => void;
}

export const usePolling = (
  callback: () => void,
  options: UsePollingOptions = {}
) => {
  const {
    enabled = true,
    interval = 2000,
    timeout = 30000,
    onTimeout,
  } = options;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (!enabled) return;

    // Execute immediately
    callback();

    // Set up interval
    intervalRef.current = setInterval(callback, interval);

    // Set up timeout
    if (timeout > 0) {
      timeoutRef.current = setTimeout(() => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        onTimeoutRef.current?.();
      }, timeout);
    }

    // Cleanup
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, interval, timeout]);
};
